/**
 * RelationshipManager - Read-only accessor for relationship data
 *
 * Provides reactive access to relationships from GraphStore.
 * For mutations, use EditableWrapper pattern: node.makeEditable()
 *
 * @example
 * ```typescript
 * // Read relationship data
 * const steps = thread.steps.nodes;  // Array of related nodes
 *
 * // Reactive access in React components
 * function ThreadSteps({ thread }) {
 *   const steps = useNodes(thread.steps); // Re-renders on DAG patches!
 *   return <>{steps.map(s => <div>{s.text}</div>)}</>;
 * }
 *
 * // Modify relationships (use EditableWrapper)
 * const editable = thread.makeEditable();
 * editable.steps.add(step1, step2);
 * await editable.commit();
 * ```
 */

import { useAtomValue, atom } from 'jotai';
import { useMemo } from 'react';
import { Relationship, BasicRelationship } from './relationship';
import { graphStore, graphStoreJotaiStore, nodeAtomFamily } from './graph-store';
import { createMaterializedView, MaterializedView } from './materialized-view';
import { queryCache } from './query-cache';

export class RelationshipManager<TSource, TTarget> {
  private _sourceInstance: TSource;
  private _propertyName: string;
  private _cardinality: 'one' | 'many';
  private _relationshipClass?: typeof Relationship;
  private _relationshipInstances: Relationship[] = [];

  constructor(
    sourceInstance: TSource,
    propertyName: string,
    cardinality: 'one' | 'many',
    relationshipClass?: typeof Relationship
  ) {
    this._sourceInstance = sourceInstance;
    this._propertyName = propertyName;
    this._cardinality = cardinality;
    this._relationshipClass = relationshipClass;

    // If relationship class provided, use its metadata
    if (relationshipClass) {
      // Use cardinality from relationship class if available
      const classCardinality = relationshipClass.cardinality;
      if (classCardinality) {
        this._cardinality = classCardinality === 'one2one' || classCardinality === 'm2one' ? 'one' : 'many';
      }
    }
  }

  /**
   * Get the property name of this relationship
   */
  get propertyName(): string {
    return this._propertyName;
  }

  /**
   * Get current value as array (for iteration)
   * Works for both one and many relationships
   */
  get nodes(): TTarget[] {
    // First try to get from GraphStore if source has an ID
    const sourceId = (this._sourceInstance as any).id;
    if (sourceId) {
      const targetNodes = graphStore.getTargetNodes<any>(sourceId, this._propertyName) as TTarget[];
      if (targetNodes.length > 0) {
        return targetNodes;
      }
    }

    // Fall back to checking instance properties (for unsaved/local data)
    const privateKey = `_${this._propertyName}`;
    const privateValue = (this._sourceInstance as any)[privateKey];
    const publicValue = (this._sourceInstance as any)[this._propertyName];

    const value = privateValue || publicValue;
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  /**
   * Get current value as single item (for one-to-one relationships)
   */
  get node(): TTarget | null {
    // The GraphStore first, as `nodes` does: a neograph query (`.all()`)
    // registers each link there, and its rows are flat — no nested object
    // for `loadOneRelationship` to set `_<name>` from. Without this a to-one
    // read after a query answered null while `.nodes` answered the target
    // (the organisation page's invites, VC-e2e 2026-09-29).
    const sourceId = (this._sourceInstance as any).id;
    if (sourceId) {
      const targetNodes = graphStore.getTargetNodes<any>(sourceId, this._propertyName) as TTarget[];
      if (targetNodes.length > 0) {
        return targetNodes[0];
      }
    }

    // Then the instance's own value (where .load() stores nested data, and
    // unsaved/local data)
    const privateKey = `_${this._propertyName}`;
    const value = (this._sourceInstance as any)[privateKey] || (this._sourceInstance as any)[this._propertyName];
    if (!value) return null;
    return Array.isArray(value) ? (value[0] || null) : value;
  }

  /**
   * Get the relationship class metadata
   */
  get relationshipClass(): typeof Relationship | undefined {
    return this._relationshipClass;
  }

  /**
   * Get all relationship instances
   */
  get relationshipInstances(): Relationship[] {
    return this._relationshipInstances;
  }
}

/**
 * Standalone hook for accessing relationship nodes
 * Always calls hooks unconditionally (satisfies React Rules of Hooks)
 *
 * @param relationshipManager Optional relationship manager
 * @returns MaterializedView of related nodes or empty view
 *
 * @example
 * ```typescript
 * function ProductSpecs({ node }: { node: PDMProduct | PDMComponent }) {
 *   // Always calls hook - handles undefined internally
 *   const requirements = useNodes(isProduct ? node.requirements : undefined);
 *   return <>{requirements.map(r => <div>{r.text}</div>)}</>;
 * }
 * ```
 */
export function useNodes<T>(
  relationshipManager?: RelationshipManager<any, T>
): MaterializedView<T> {
  const sourceId = relationshipManager ? (relationshipManager['_sourceInstance'] as any)?.id || null : null;
  const propertyName = relationshipManager?.propertyName || '';

  // ALWAYS call all hooks in the same order, regardless of relationshipManager
  const relListAtom = useMemo(
    () => sourceId ? graphStore.getRelationshipListAtom(sourceId, propertyName) : atom([]),
    [sourceId, propertyName]
  );

  const relIds = useAtomValue(relListAtom, { store: graphStoreJotaiStore });

  const targetNodeIds = useMemo(() => {
    if (!sourceId) return [];
    const nodeIds = relIds
      .map(relId => {
        const rel = graphStore.getRelationship(relId);
        return rel?.targetId;
      })
      .filter((id): id is string => id !== null && id !== undefined);

    return nodeIds;
  }, [sourceId, relIds]);

  const combinedNodesAtom = useMemo(() => {
    return atom((get) => {
      const nodes = targetNodeIds
        .map(nodeId => {
          const nodeAtom = nodeAtomFamily(nodeId);
          const node = get(nodeAtom) as T;
          return node;
        })
        .filter((node): node is T => node !== null && node !== undefined);

      return nodes;
    });
  }, [targetNodeIds]);

  const targetNodes = useAtomValue(combinedNodesAtom, { store: graphStoreJotaiStore });

  return createMaterializedView(targetNodes);
}

/**
 * Standalone hook for accessing a single relationship node (one-to-one relationships)
 * Always calls hooks unconditionally (satisfies React Rules of Hooks)
 *
 * This is for GraphStore relationships - use useQueryResult() for QueryCache results.
 *
 * @param relationshipManager Optional relationship manager
 * @returns Single related node or null
 *
 * @example
 * ```typescript
 * function ToolCallResponse({ part }: { part: ToolCallPart }) {
 *   // One-to-one relationship - returns single item
 *   const response = useNode(part.response);
 *   return response ? <div>{response.content}</div> : null;
 * }
 * ```
 */
export function useNode<T>(
  relationshipManager?: RelationshipManager<any, T>
): T | null {
  const nodes = useNodes(relationshipManager);
  return nodes[0] || null;
}

/**
 * Standalone hook for accessing relationship nodes paired with their edge properties
 * Always calls hooks unconditionally (satisfies React Rules of Hooks)
 *
 * @param relationshipManager Optional relationship manager
 * @returns Array of {node, properties} pairs
 *
 * @example
 * ```typescript
 * function ProjectMembers({ user }: { user: User }) {
 *   const projects = useNodesWithRel(user.projects);
 *   return <>{projects.map(({ node, properties }) => (
 *     <div>{node.name} - role: {properties.role}</div>
 *   ))}</>;
 * }
 * ```
 */
export function useNodesWithRel<T>(
  relationshipManager?: RelationshipManager<any, T>
): Array<{ node: T; properties: Record<string, any> }> {
  const sourceId = relationshipManager ? (relationshipManager['_sourceInstance'] as any)?.id || null : null;
  const propertyName = relationshipManager?.propertyName || '';

  // ALWAYS call all hooks in the same order, regardless of relationshipManager
  const relListAtom = useMemo(
    () => sourceId ? graphStore.getRelationshipListAtom(sourceId, propertyName) : atom([]),
    [sourceId, propertyName]
  );

  const relIds = useAtomValue(relListAtom, { store: graphStoreJotaiStore });

  // Collect relationship instances and target node IDs together
  const relEntries = useMemo(() => {
    if (!sourceId) return [];
    return relIds
      .map(relId => {
        const rel = graphStore.getRelationship(relId);
        if (!rel) return null;
        return { targetId: rel.targetId, properties: rel.properties || {} };
      })
      .filter((entry): entry is { targetId: string; properties: Record<string, any> } => entry !== null);
  }, [sourceId, relIds]);

  // Subscribe to node atoms for reactivity (same pattern as useNodes)
  const combinedAtom = useMemo(() => {
    return atom((get): Array<{ node: T; properties: Record<string, any> }> => {
      const results: Array<{ node: T; properties: Record<string, any> }> = [];
      for (const entry of relEntries) {
        const node = get(nodeAtomFamily(entry.targetId)) as T | null;
        if (node) {
          results.push({ node, properties: entry.properties });
        }
      }
      return results;
    });
  }, [relEntries]);

  return useAtomValue(combinedAtom, { store: graphStoreJotaiStore });
}

/**
 * Standalone hook for accessing a single query-specific result (one-to-one relationships)
 * Always calls hooks unconditionally (satisfies React Rules of Hooks)
 *
 * This hook subscribes to the QueryCache and returns a single result.
 *
 * WHEN TO USE:
 * - Use this when you need query-specific filtering (e.g., "give me the FIRST step with role=user")
 * - For simple one-to-one eager-loaded relationships, use useNode() instead (simpler, no queryKey)
 *
 * @param relationshipManager Relationship manager (can be undefined for conditional rendering)
 * @param queryKey Unique identifier for the query (required)
 * @returns Single target node or null
 *
 * @example
 * ```typescript
 * // When you need query-specific filtering:
 * function FirstUserStep({ thread, queryKey }: { thread: Thread; queryKey: string }) {
 *   const firstUserStep = useResult(thread.steps, queryKey); // Filtered by query
 *   return firstUserStep ? <div>{firstUserStep.text}</div> : null;
 * }
 *
 * // For simple eager-loaded relationships, prefer useNode:
 * function ThreadAttachment({ thread }: { thread: Thread }) {
 *   const product = useNode(thread.currently_attached_to); // Simpler!
 *   return product ? <div>{product.name}</div> : null;
 * }
 * ```
 */
export function useResult<T>(
  relationshipManager: RelationshipManager<any, T> | undefined,
  queryKey: string
): T | null {
  const results = useResults(relationshipManager, queryKey);
  return results[0] || null;
}

/**
 * Standalone hook for accessing query-specific filtered/ordered results
 * Always calls hooks unconditionally (satisfies React Rules of Hooks)
 *
 * This hook subscribes to the QueryCache and returns results in server-provided order.
 *
 * WHEN TO USE:
 * - Use this when you need query-specific filtering/ordering (e.g., "steps WHERE role=user ORDER BY created_at LIMIT 10")
 * - Use this when server-side ordering matters (e.g., steps in chronological order)
 * - For all eager-loaded relationships where ordering doesn't matter, use useNodes() instead
 *
 * @param relationshipManager Relationship manager (can be undefined for conditional rendering)
 * @param queryKey Unique identifier for the query (required)
 * @returns Array of target nodes in query order
 *
 * @example
 * ```typescript
 * // When you need query-specific ordering:
 * function ThreadSteps({ thread, queryKey }: { thread: Thread; queryKey: string }) {
 *   // Returns steps in server-specified order from this specific query
 *   const steps = useResults(thread.steps, queryKey);
 *   return <>{steps.map(s => <div key={s.id}>{s.text}</div>)}</>;
 * }
 *
 * // For simple eager-loaded relationships, prefer useNodes:
 * function ThreadSteps({ thread }: { thread: Thread }) {
 *   const steps = useNodes(thread.steps); // Simpler, no queryKey needed!
 *   return <>{steps.map(s => <div key={s.id}>{s.text}</div>)}</>;
 * }
 * ```
 */
export function useResults<T>(
  relationshipManager: RelationshipManager<any, T> | undefined,
  queryKey: string
): T[] {
  const sourceId = relationshipManager ? (relationshipManager['_sourceInstance'] as any)?.id || null : null;
  // Automatically extract propertyName from RelationshipManager
  const propertyName = relationshipManager?.propertyName;

  // ALWAYS call all hooks in the same order, regardless of parameters
  const relListAtom = useMemo(() => {
    if (propertyName && sourceId) {
      // Traversal path format: "sourceNodeId:propertyName" (matches populateGraphStore)
      const traversalPath = `${sourceId}:${propertyName}`;
      return queryCache.getRelationshipListAtom(queryKey, traversalPath);
    }
    return atom([]);
  }, [queryKey, propertyName, sourceId]);

  const relIds = useAtomValue(relListAtom, { store: graphStoreJotaiStore }) as string[];

  const targetNodeIds = useMemo(() => {
    if (!propertyName || !sourceId) return [];
    return relIds
      .map((relId: string) => {
        const rel = graphStore.getRelationship(relId);
        if (!rel) return null;
        return rel.targetId;
      })
      .filter((id: string | null | undefined): id is string => id !== null && id !== undefined);
  }, [queryKey, propertyName, sourceId, relIds]);

  const combinedNodesAtom = useMemo(() => {
    return atom((get) => {
      return targetNodeIds
        .map((nodeId: string) => {
          const nodeAtom = nodeAtomFamily(nodeId);
          return get(nodeAtom) as T;
        })
        .filter((node: T | null | undefined): node is T => node !== null && node !== undefined);
    });
  }, [targetNodeIds]);

  const targetNodes = useAtomValue(combinedNodesAtom, { store: graphStoreJotaiStore });

  return targetNodes;
}
